import {
  List,
  FlatList,
  useBreakpointProp,
  PressableIcon,
  useHover,
} from '@bonanza-social/moonlight';
import { memo, useCallback, useRef } from 'react';
import ShopFilterItem from '../ShopFilterItem/ShopFilterItem';
import { useShopFilters, useShopRouteParams } from '../../hooks';
import { useListNavigation } from '@bonanza-social/navigation';
import { ListRenderItem, View } from 'react-native';

import {
  CONTAINER_STYLE,
  LEFT_ARROW_CONTAINER_STYLE,
  RIGHT_ARROW_CONTAINER_STYLE,
} from './ShopFilterItemList.content';
import { ShopFilterType } from '../../state/types';

function ShopFilterItemList() {
  const { filterParam } = useShopRouteParams();
  const { filters } = useShopFilters();

  const listRef = useRef<FlatList>(null);
  const containerRef = useRef<View>(null);

  const showArrows = useBreakpointProp({
    mobile: false,
    tablet: false,
    desktop: true,
  });

  const { isHovered, ...listHoverProps } = useHover();

  const {
    handleScroll,
    handleLayout,
    handleContentSizeChange,
    showLeftArrow,
    showRightArrow,
    handleArrowForward,
    handleArrowBack,
  } = useListNavigation({
    listRef,
    containerRef,
    itemWidth: undefined,
    showArrows,
    itemsCount: filters ? filters.length : 0,
    enableDragging: true,
    enableArrows: true,
  });

  const renderFilter = useCallback<ListRenderItem<ShopFilterType>>(
    ({ item }) => (
      <ShopFilterItem filter={item} isSelected={item === filterParam} />
    ),
    [filterParam],
  );

  const keyExtractor = useCallback(
    (item: ShopFilterType, index: number) => `${item ?? ''}-${index}`,
    [],
  );

  if (!filters || filters.length === 0) return null;

  return (
    <View
      ref={containerRef}
      style={CONTAINER_STYLE}
      onLayout={handleLayout}
      {...listHoverProps}
    >
      <List
        ref={listRef}
        data={filters}
        keyExtractor={keyExtractor}
        renderItem={renderFilter}
        onScroll={handleScroll}
        onContentSizeChange={handleContentSizeChange}
        scrollEventThrottle={16}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 'space-300',
          paddingBottom: 'space-200',
        }}
      />
      {showArrows && showLeftArrow && (
        <View style={LEFT_ARROW_CONTAINER_STYLE}>
          <PressableIcon
            name="arrowBackOutlined"
            opacity={isHovered ? 0.9 : 0}
            alignItems="center"
            justifyContent="center"
            backgroundColor="overlayBackground"
            color="level_0"
            borderRadius="radius-600"
            iconHeight="space-500"
            iconWidth="space-500"
            width="space-800"
            height="space-800"
            onPress={handleArrowBack}
          />
        </View>
      )}
      {showArrows && showRightArrow && (
        <View style={RIGHT_ARROW_CONTAINER_STYLE}>
          <PressableIcon
            name="arrowForwardOutlined"
            opacity={isHovered ? 0.9 : 0}
            alignItems="center"
            justifyContent="center"
            backgroundColor="overlayBackground"
            color="level_0"
            borderRadius="radius-600"
            width="space-800"
            height="space-800"
            onPress={handleArrowForward}
          />
        </View>
      )}
    </View>
  );
}

export default memo(ShopFilterItemList);
